"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import SectionHeader from "@/components/ui/section-header";
import { ArrowRight, BookOpen } from "@/components/icons";
import { RESIDENTIAL_RELATED_GUIDES } from "./data";

interface Props {
  variant: "residential";
}

const GUIDES_BY_VARIANT = {
  residential: RESIDENTIAL_RELATED_GUIDES,
};

export default function RelatedGuides({ variant }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const guides = GUIDES_BY_VARIANT[variant];

  return (
    <section
      className="py-section-sm border-border border-b"
      aria-labelledby="related-guides-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 max-w-2xl">
          <SectionHeader
            eyebrow="Guides"
            id="related-guides-heading"
            heading="Related reading."
            body="In-depth comparisons for common residential proxy workflows."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {guides.map((guide, i) => (
            <motion.div
              key={guide.id}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={scrollRevealViewport}
              transition={{ duration: 0.45, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="group border-border bg-card hover:border-foreground/20 relative overflow-hidden rounded-2xl border p-6 transition-colors duration-200"
            >
              <Link href={guide.href} className="absolute inset-0 z-10" aria-label={guide.title} />

              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{
                  /* Foreground-alpha, not the fixed near-white BRAND: a white
                     glow over a white card is invisible, so on the light theme
                     the hover affordance simply vanished. */
                  background:
                    "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--foreground) 5%, transparent), transparent 70%)",
                }}
              />

              <div className="relative mb-4 flex items-center justify-between">
                <div className="bg-brand/10 text-brand inline-flex h-10 w-10 items-center justify-center rounded-lg">
                  <BookOpen size={18} aria-hidden="true" />
                </div>
                <span className="text-muted-foreground flex items-center gap-1 text-xs font-medium">
                  Read guide
                  <ArrowRight size={12} aria-hidden="true" />
                </span>
              </div>

              <h3 className="text-foreground relative mb-2 text-lg font-semibold">{guide.title}</h3>
              <p className="text-muted-foreground relative text-sm leading-relaxed">
                {guide.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
